import React, { useLayoutEffect, useEffect, useRef, useCallback } from 'react';
import {
  Plane,
  Hospital,
  GraduationCap,
  ShoppingBag,
  Building2,
  Fuel,
} from 'lucide-react';
import '../styles/HomeLandingPage.css';

const navItems = [
  { href: '#problem', label: 'Problem' },
  { href: '#solution', label: 'Solution' },
  { href: '#why', label: 'Why It Matters' },
  { href: '#contribute', label: 'Contribute' },
];

const problemPlaces = [
  { name: 'Airports', icon: <Plane size={20} strokeWidth={1.9} /> },
  { name: 'Hospitals', icon: <Hospital size={20} strokeWidth={1.9} /> },
  { name: 'Universities', icon: <GraduationCap size={20} strokeWidth={1.9} /> },
  { name: 'Shopping Centres', icon: <ShoppingBag size={20} strokeWidth={1.9} /> },
  { name: 'Office Buildings', icon: <Building2 size={20} strokeWidth={1.9} /> },
  { name: 'Service Areas', icon: <Fuel size={20} strokeWidth={1.9} /> },
];

type SolutionCard = {
  title: string;
  body: string;
  image?: string;
};

const solutionCards: SolutionCard[] = [
  {
    title: 'Find a place to pray before you leave',
    body: 'Open the map and see real prayer spaces on your route, from city centres to service stations.',
  },
  {
    title: 'Add missing prayer rooms in minutes',
    body: 'Submit a new location, fill key details, and help the next traveller pray with confidence.',
  },
  {
    title: 'Keep the map accurate together',
    body: 'Update incorrect entries and keep opening times, facilities, and access notes useful for everyone.',
  },
];

const whySteps = [
  { num: '01', text: 'To make travelling lighter' },
  { num: '02', text: 'To reduce hesitation' },
  { num: '03', text: 'To give certainty before departure' },
];

function useReveal() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('hl-visible');
          observer.unobserve(el);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return ref;
}

function RevealSection({
  children,
  className = '',
  id,
}: {
  children: React.ReactNode;
  className?: string;
  id?: string;
}) {
  const ref = useReveal();
  return (
    <div ref={ref} className={`hl-reveal ${className}`} id={id}>
      {children}
    </div>
  );
}

type HomeLandingPageProps = {
  mosqueCount?: number;
  isMosqueCountLoading?: boolean;
};

export default function HomeLandingPage({ mosqueCount, isMosqueCountLoading = false }: HomeLandingPageProps) {
  const hasRealCount = typeof mosqueCount === 'number' && mosqueCount > 0;
  const mappedCount = hasRealCount && !isMosqueCountLoading
    ? new Intl.NumberFormat('en-GB').format(mosqueCount)
    : '2400+';

  const stats = [
    { value: mappedCount, label: 'Prayer spaces mapped' },
    { value: 'UK-wide', label: 'Coverage growing' },
    { value: 'Growing', label: 'Every day', accent: true },
  ];

  useLayoutEffect(() => {
    const cls = 'home-landing-route';
    const root = document.getElementById('root');
    document.documentElement.classList.add(cls);
    document.body.classList.add(cls);
    root?.classList.add(cls);
    return () => {
      document.documentElement.classList.remove(cls);
      document.body.classList.remove(cls);
      root?.classList.remove(cls);
    };
  }, []);

  const scrollTo = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    const href = e.currentTarget.getAttribute('href');
    if (!href?.startsWith('#')) return;
    e.preventDefault();
    const target = document.getElementById(href.slice(1));
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  return (
    <main className="hl" aria-label="Mosque Map landing page">
      {/* ── BACKGROUND ATMOSPHERE ── */}
      <div className="hl-ambient" aria-hidden="true">
        <div className="hl-ambient__glow hl-ambient__glow--1" />
        <div className="hl-ambient__glow hl-ambient__glow--2" />
        <div className="hl-ambient__grain" />
      </div>

      {/* ── NAV ── */}
      <header className="hl-nav">
        <div className="hl-nav__inner">
          <a className="hl-nav__brand" href="/home">
            <span className="hl-nav__brand-icon">🕌</span>
            <span className="hl-nav__brand-text">Mosque Map</span>
          </a>
          <nav className="hl-nav__links" aria-label="Main navigation">
            {navItems.map((item) => (
              <a key={item.href} href={item.href} onClick={scrollTo}>
                {item.label}
              </a>
            ))}
          </nav>
          <div className="hl-nav__actions">
            <a className="hl-nav__cta" href="/">
              View Map
            </a>
          </div>
        </div>
      </header>

      {/* ── HERO ── */}
      <section className="hl-hero">
        <div className="hl-hero__content">
          <div className="hl-hero__badge">
            <span className="hl-hero__badge-dot" />
            Community-driven project
          </div>
          <h1 className="hl-hero__title">
            <span className="hl-hero__title-top">Every Prayer Room</span>
            <span className="hl-hero__title-bottom">in the UK</span>
          </h1>
          <div className="hl-hero__description">
            <p>Find prayer spaces anywhere.</p>
            <p>Add missing ones.</p>
            <p>Keep it alive.</p>
          </div>
          <div className="hl-hero__actions">
            <a className="hl-btn hl-btn--primary" href="/">
              View the Map
            </a>
            <a className="hl-btn hl-btn--secondary" href="#contribute" onClick={scrollTo}>
              Contribute
            </a>
          </div>
        </div>

        <div className="hl-stats">
          {stats.map((s, i) => (
            <div key={s.value} className="hl-stats__item" style={{ '--index': i } as React.CSSProperties}>
              <div className="hl-stats__value-wrap">
                <span className={`hl-stats__value${s.accent ? ' hl-stats__value--accent' : ''}`}>
                  {s.value}
                </span>
                {s.accent && <span className="hl-stats__value-pulse" />}
              </div>
              <span className="hl-stats__label">{s.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ── PROBLEM ── */}
      <RevealSection className="hl-section hl-problem" id="problem">
        <div className="hl-section__header">
          <span className="hl-section__tag">The Problem</span>
          <h2 className="hl-section__title">Prayer spaces exist across the UK</h2>
        </div>

        <div className="hl-problem__content">
          <div className="hl-problem__text">
            <p className="hl-problem__lead">
              In airports, hospitals, motorway service areas, universities, shopping centres, office
              buildings, restaurants, and temporary halls opened only for Jumu&apos;ah.
            </p>
            <div className="hl-problem__highlight">
              <p className="hl-problem__emphasis">Yet many remain invisible online.</p>
              <p>Not because they are rare. Because they are undocumented.</p>
            </div>
            <blockquote className="hl-quote">
              <p>&ldquo;When information is scattered, uncertainty replaces ease.&rdquo;</p>
            </blockquote>
          </div>
          <div className="hl-problem__visual">
            <ul className="hl-location-grid">
              {problemPlaces.map((place, i) => (
                <li key={place.name} className="hl-location-item" style={{ '--index': i } as React.CSSProperties}>
                  <span className="hl-location-item__icon" aria-hidden="true">{place.icon}</span>
                  <span className="hl-location-item__text">{place.name}</span>
                 </li>
              ))}
            </ul>
          </div>
        </div>
      </RevealSection>

      {/* ── SOLUTION ── */}
      <RevealSection className="hl-section hl-solution" id="solution">
        <div className="hl-section__header hl-section__header--center">
          <span className="hl-section__tag">The Solution</span>
          <h2 className="hl-section__title">A living, community-driven map</h2>
          <p className="hl-section__subtitle">
            We&apos;re building a comprehensive map of prayer spaces across the UK.
          </p>
        </div>

        <div className="hl-cards">
          {solutionCards.map((c, i) => (
            <article key={c.title} className="hl-card" style={{ '--index': i } as React.CSSProperties}>
              {c.image ? (
                <img
                  className="hl-card__image"
                  src={c.image}
                  alt="Hagia Sophia and surrounding historic mosque skyline"
                  loading="lazy"
                />
              ) : (
                <div className="hl-card__icon">
                  {i === 0 && '🕌'}
                  {i === 1 && '🤝'}
                  {i === 2 && '🌱'}
                </div>
              )}
              <h3 className="hl-card__title">{c.title}</h3>
              <p className="hl-card__body">{c.body}</p>
            </article>
          ))}
        </div>
      </RevealSection>

      {/* ── WHY IT MATTERS ── */}
      <RevealSection className="hl-section hl-why" id="why">
        <div className="hl-why__container">
          <div className="hl-why__header">
            <span className="hl-section__tag">Why It Matters</span>
            <h2 className="hl-section__title">Prayer is not optional convenience</h2>
          </div>

          <div className="hl-why__grid">
            <div className="hl-why__content">
              <p className="hl-why__lead">For Muslims, prayer is structure. It is grounding. It is the anchor of the day.</p>
              <p>
                The Prophet (peace be upon him) taught us that the entire earth was made a place of
                prayer &mdash; a teaching meant to bring ease, not uncertainty.
              </p>
              <p>Yet in the modern world, we still struggle to locate a quiet room behind a corridor.</p>
            </div>
            <div className="hl-why__steps">
              {whySteps.map((s, i) => (
                <div key={s.num} className="hl-step" style={{ '--index': i } as React.CSSProperties}>
                  <div className="hl-step__num-wrap">
                    <span className="hl-step__num">{s.num}</span>
                    <div className="hl-step__line" />
                  </div>
                  <p className="hl-step__text">{s.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </RevealSection>

      {/* ── SADAQAH JARIYAH ── */}
      <RevealSection className="hl-charity" id="contribute">
        <div className="hl-charity__bg" />
        <div className="hl-charity__inner">
          <div className="hl-charity__content">
            <span className="hl-section__tag hl-section__tag--light">A Continuous Charity</span>
            <h2 className="hl-charity__title">Sadaqah Jariyah</h2>

            <div className="hl-charity__grid">
              <div className="hl-charity__benefit">
                <span className="hl-charity__benefit-icon">✨</span>
                <p>
                  When someone finds a prayer space because you added it &mdash;
                  <strong>that benefit continues.</strong>
                </p>
              </div>
              <div className="hl-charity__benefit">
                <span className="hl-charity__benefit-icon">🤲</span>
                <p>
                  When someone does not miss prayer because you updated a detail &mdash;
                  <strong>that reward continues.</strong>
                </p>
              </div>
              <div className="hl-charity__benefit">
                <span className="hl-charity__benefit-icon">📖</span>
                <p>
                  When knowledge remains accessible long after we are gone &mdash;
                  <strong>that impact continues.</strong>
                </p>
              </div>
            </div>

            <div className="hl-charity__footer">
              <div className="hl-charity__coda">
                <p>Small contributions.</p>
                <p>Lasting impact.</p>
              </div>
              <a className="hl-btn hl-btn--white" href="/">
                Join the Mission
              </a>
            </div>
          </div>
        </div>
      </RevealSection>

      {/* ── FOOTER ── */}
      <footer className="hl-footer">
        <div className="hl-footer__inner">
          <div className="hl-footer__brand">
            <div className="hl-footer__logo">
              <span className="hl-footer__logo-icon">🕌</span>
              <h4>Mosque Map</h4>
            </div>
            <p>A community project for the Ummah. Built with care.</p>
          </div>
          <div className="hl-footer__nav">
            <nav className="hl-footer__links" aria-label="Footer links">
              <a href="#problem" onClick={scrollTo}>About</a>
              <a href="#contribute" onClick={scrollTo}>Contribute</a>
              <a href="mailto:mustafa.mosque.map@gmail.com">Contact</a>
            </nav>
            <p className="hl-footer__copy">© {new Date().getFullYear()} Mosque Map. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </main>
  );
}
